import Add16 from '@carbon/icons-react/es/add/16';
import Partnership16 from '@carbon/icons-react/es/partnership/16';
import { Tabs as AntdTabs } from 'antd';
import { debounce } from 'lodash';
import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';
import { UserType } from 'api/models';
import { Project } from 'api/project';
import { Avatar } from 'components/atoms/avatar';
import { Button } from 'components/atoms/button';
import { Form } from 'components/atoms/form';
import { Option } from 'components/atoms/select';
import { useAccount } from 'hooks/use-account';
import { useUser } from 'hooks/use-user';
import { useGetAllActivePartnerCompanies } from 'queries/partner';
// eslint-disable-next-line import/no-internal-modules
import { useAddPartnersToProjectMutation } from 'queries/project';
import { PartnerSelect } from 'routes/projects/create/partner-select';
import { byLabel } from 'utils/comparators/by-label';
// eslint-disable-next-line import/no-internal-modules
import { LogoContainer } from '../project-create/form/team-details/logo-container';
import { MemberType } from '../project-settings';
import { MembersList } from './members-list/members-list';

// NOTE:: Equal width tabs can also be moved as a separate atom if the need arises
const FormWrapper = styled(Form)`
  height: 100%;

  .ant-tabs-tab {
    flex: 1;
    overflow: hidden;
  }
`;
const Tabs = styled(AntdTabs)<{ tabWithIconOnly: boolean; tabWidth: number }>`
  .ant-tabs-nav {
    margin: 0;
  }
  .ant-tabs-nav-wrap {
    .ant-tabs-nav-list .ant-tabs-tab {
      min-width: 56px;
      width: ${({ tabWidth }) => `${tabWidth}px`};
      ${({ tabWithIconOnly }) =>
        tabWithIconOnly ? 'justify-content: center;' : ''};
    }
  }
  .ant-tabs-nav-operations {
    display: none;
  }
  &,
  .ant-tabs-content-holder,
  .ant-tabs-content {
    height: unset;
  }
`;

const TeamDetails = styled.div`
  height: 100%;
  margin-bottom: var(--spacing-08);
`;
const TeamMemberContainer = styled.div`
  max-height: calc(100% - 50px);
  overflow-y: auto;
  position: relative;
`;

const { TabPane } = Tabs;
const TabSortOrder = [UserType.Native, UserType.External, UserType.Partner];

export const TabbedMemberList: FC<{
  className?: string;
  readOnly?: boolean;
  project: Project;
}> = ({ className, project }) => {
  const { company: vendorCompany } = useAccount();
  const { user } = useUser();
  const memberTypes: MemberType[] = useMemo(
    () =>
      [
        {
          type: 'nativeMembers',
          userType: UserType.Native,
          company: vendorCompany,
        },
        {
          type: 'externalMembers',
          userType: UserType.External,
          company: project?.customer,
        },
      ].concat(
        project?.partners.map((partner) => ({
          type: `partner_${partner.companyId}`,
          userType: UserType.Partner,
          company: partner,
        })),
      ),
    [project?.customer, project?.partners, vendorCompany],
  );
  const sortTabsByLogin = useCallback(
    (_memberTypes: MemberType[]) =>
      _memberTypes.sort((a, b) => {
        if (a.company.companyId === user.company.companyId) {
          return -1;
        }

        return (
          TabSortOrder.indexOf(a.userType) - TabSortOrder.indexOf(b.userType)
        );
      }),
    [user.company.companyId],
  );
  const sortedTabs = useMemo(
    () => sortTabsByLogin(memberTypes),
    [memberTypes, sortTabsByLogin],
  );
  const { data: partnerCompanies, isLoading: partnerCompaniesLoading } =
    useGetAllActivePartnerCompanies();

  const partnerOptions: Option[] = useMemo(() => {
    const addedPartnerIds = project.partners.map(({ companyId }) => companyId);

    return (partnerCompanies ?? [])
      .filter(({ companyId }) => !addedPartnerIds.includes(companyId))
      .map(({ companyName, companyId, companyLogoUrl }) => ({
        label: companyName,
        value: companyId,
        imageUrl: companyLogoUrl,
      }))
      .sort(byLabel);
  }, [partnerCompanies, project.partners]);
  const { mutate: addPartners, isLoading: partnerAdditionInprogress } =
    useAddPartnersToProjectMutation();
  const onPartnerSelect = useCallback(
    (partnerCompanyOptions) => {
      addPartners({
        projectId: project.projectId,
        partners: partnerCompanyOptions.map(({ value }) => value),
      });
    },
    [addPartners, project.projectId],
  );
  const partnerSelection = useMemo(
    () => (
      <PartnerSelect
        options={partnerOptions}
        onChange={onPartnerSelect}
        placement="rightTop"
        triggerComponent={
          <Button
            type="link"
            icon={
              <>
                <Add16 />
                <Partnership16 />
              </>
            }
            loading={partnerCompaniesLoading || partnerAdditionInprogress}
          />
        }
      />
    ),
    [
      onPartnerSelect,
      partnerAdditionInprogress,
      partnerCompaniesLoading,
      partnerOptions,
    ],
  );
  const canAddPartners = user.userType === UserType.Native;
  const teamMemberContainer = useRef<HTMLDivElement>(null);
  const [activeTabKey, setActiveTabKey] = useState<string>();
  const [isScrolling, setScrolling] = useState(false);
  const scrollingStateRef = useRef(isScrolling);

  scrollingStateRef.current = isScrolling;
  const onTeamMembersScroll = useCallback(() => {
    if (teamMemberContainer.current == null || scrollingStateRef.current) {
      setScrolling(false);

      return;
    }
    const element = teamMemberContainer.current;

    if (
      Math.abs(
        element.scrollHeight - element.clientHeight - element.scrollTop,
      ) < 1
    ) {
      setActiveTabKey(sortedTabs[sortedTabs.length - 1].type);

      return;
    }
    const topElement = Array.from(element.children).find(
      (x) =>
        element.scrollTop <=
        (x as HTMLDivElement).offsetTop + (x as HTMLDivElement).offsetHeight,
    );

    setActiveTabKey(
      topElement?.getAttribute('data-section-id') ?? sortedTabs[0].type,
    );
  }, [sortedTabs]);
  const onTabClicked = useCallback((key) => {
    const selectedElement = document.querySelector(
      `[data-section-id=${key}]`,
    ) as HTMLDivElement;

    if (teamMemberContainer.current == null || selectedElement == null) {
      return;
    }
    setScrolling(true);
    teamMemberContainer.current.scrollTo({
      top: selectedElement.offsetTop,
      left: 0,
      behavior: 'smooth',
    });
    setActiveTabKey(key);
  }, []);

  const canShowPartnerAdd =
    canAddPartners && (partnerCompanies ?? []).length !== 0;
  const [tabWidth, setTabWidth] = useState(0);

  useEffect(() => {
    setTabWidth(
      Math.floor(
        ((teamMemberContainer.current?.offsetWidth ?? 0) -
          (canShowPartnerAdd ? 40 : 0) -
          sortedTabs.length) /
          sortedTabs.length,
      ),
    );
  }, [canShowPartnerAdd, sortedTabs.length]);

  const debouncedTeamMembersScroll = debounce(() => {
    onTeamMembersScroll();
  }, 100);

  return (
    <FormWrapper className={className}>
      <Tabs
        defaultActiveKey={sortedTabs[0].type}
        activeKey={activeTabKey}
        tabBarGutter={1}
        tabBarExtraContent={{
          right: canShowPartnerAdd ? partnerSelection : <></>,
        }}
        centered
        onTabClick={onTabClicked}
        tabWithIconOnly={sortedTabs.length > 2}
        tabWidth={tabWidth}
      >
        {sortedTabs.map((memberType) => (
          <TabPane
            tab={
              sortedTabs.length < 3 ? (
                <LogoContainer
                  src={memberType.company?.companyLogoUrl}
                  name={memberType.company?.companyName}
                  translationId="projects.create.team_no_suffix"
                  translationValues={{
                    company: memberType.company?.companyName || 'Client',
                  }}
                  showPartnerTag={false}
                />
              ) : (
                <Avatar
                  src={memberType.company?.companyLogoUrl}
                  name={memberType.company?.companyName}
                  shape="square"
                />
              )
            }
            key={memberType.type}
          />
        ))}
      </Tabs>
      <TeamMemberContainer
        ref={teamMemberContainer}
        onScroll={debouncedTeamMembersScroll}
      >
        {sortedTabs.map((memberType) => (
          <TeamDetails key={memberType.type} data-section-id={memberType.type}>
            <MembersList memberType={memberType} project={project} />
          </TeamDetails>
        ))}
      </TeamMemberContainer>
    </FormWrapper>
  );
};
